<script setup lang="ts">
const route = useRoute()

const { data: page } = await useAsyncData(`for-${route.params.slug}`, () =>
  queryCollection('landing').path(route.path).first()
)

if (!page.value) {
  throw createError({ statusCode: 404, statusMessage: 'Page not found', fatal: true })
}

const title = page.value.seo?.title || page.value.title
const description = page.value.seo?.description || page.value.description

useSeoMeta({
  title,
  // seo.title in these pages already ends with "| mychar.gg"; avoid the global
  // "%s - mychar.gg" template double-branding.
  titleTemplate: (t?: string | null) => {
    if (!t) return 'mychar.gg'
    return /mychar\.gg\s*$/i.test(t) ? t : `${t} | mychar.gg`
  },
  ogTitle: title,
  description,
  ogDescription: description
})

defineOgImageComponent('Saas')

// FAQPage schema from the page's FAQ, plus a WebPage node.
const faq = computed(() => page.value?.faq ?? [])
useSchemaOrg([
  ...(faq.value.length ? [defineWebPage({ '@type': 'FAQPage' })] : [defineWebPage({})]),
  ...faq.value.map(item => defineQuestion({ name: item.label, acceptedAnswer: item.content }))
])
</script>

<template>
  <div v-if="page">
    <UPageHero
      :title="page.title"
      :description="page.description"
      :ui="{ container: 'py-16 sm:py-20' }"
    />

    <UContainer class="pb-16">
      <div class="mx-auto max-w-3xl prose prose-invert dark:prose-invert">
        <ContentRenderer
          v-if="page.body"
          :value="page"
        />
      </div>

      <div
        v-if="faq.length"
        class="mx-auto mt-16 max-w-3xl"
      >
        <h2 class="mb-6 text-2xl font-bold text-highlighted">
          Frequently asked questions
        </h2>
        <UAccordion
          :items="faq.map(f => ({ label: f.label, content: f.content }))"
          type="multiple"
          class="flex flex-col gap-3"
          :ui="{
            item: 'rounded-xl border border-default bg-elevated/40 px-4 border-b-0 last:border-b-0 shadow-(--surface-shadow)',
            trigger: 'text-base text-highlighted',
            body: 'text-base text-muted'
          }"
        />
      </div>
    </UContainer>

    <CtaPanel />
  </div>
</template>
